import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ToastContainer, ToastProps } from "@/components/ui/toast";
import AuthLayout from "@/components/layout/AuthLayout";
import StepIndicator from "@/components/onboarding/StepIndicator";
import LibraryInfoStep from "@/components/onboarding/LibraryInfoStep";
import AdminInfoStep from "@/components/onboarding/AdminInfoStep";
import ContactInfoStep from "@/components/onboarding/ContactInfoStep";
import PreviewStep from "@/components/onboarding/PreviewStep";
import { createTenant } from "@/lib/api/onboarding";
import { OnboardingData } from "@/types/onboarding";
import { CheckCircle2, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";

const initialFormData: OnboardingData = {
  libraryName: "",
  libraryType: "university",
  domainPrefix: "",
  adminFirstName: "",
  adminLastName: "",
  adminEmail: "",
  adminPassword: "",
  confirmPassword: "",
  contactPhone: "",
  address: "",
  city: "",
  state: "",
  country: "",
  postalCode: "",
  defaultLocale: "en",
  acceptTerms: false,
};

const stepMeta = [
  {
    title: "Library Information",
    description: "Tell us about your library so we can set up your workspace.",
  },
  {
    title: "Admin Account",
    description: "Create the administrator credentials for your library.",
  },
  {
    title: "Contact & Location",
    description: "Where is your library located? This helps configure regional settings.",
  },
  {
    title: "Review & Confirm",
    description: "Double-check your details before we create your library.",
  },
];

export default function OnboardingPage() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<OnboardingData>(initialFormData);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [toasts, setToasts] = useState<ToastProps[]>([]);

  useEffect(() => {
    localStorage.removeItem("philolms_onboarding_data");
  }, []);

  const showToast = (
    description: string,
    variant: "default" | "destructive" | "success" = "default",
    title?: string
  ) => {
    const id = Math.random().toString(36).substring(7);
    const newToast: Omit<ToastProps, "onClose"> = { id, title, description, variant };
    setToasts((prev) => [...prev, { ...newToast, onClose: () => removeToast(id) }]);
    setTimeout(() => removeToast(id), 5000);
  };

  const removeToast = (id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  const updateFormData = (updates: Partial<OnboardingData>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
    setError(null);
  };

  const goToStep = (step: number) => {
    setCurrentStep(step);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleNext = () => {
    if (currentStep < 4) goToStep(currentStep + 1);
  };

  const handleBack = () => {
    if (currentStep > 1) goToStep(currentStep - 1);
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    setError(null);

    try {
      await createTenant(formData);

      showToast(
        "Library created successfully! Please check your email to verify your account.",
        "success",
        "Success"
      );
      setSuccess(true);

      setTimeout(() => {
        navigate("/verify-email", {
          state: {
            email: formData.adminEmail,
            message: "Please check your email to verify your account before logging in.",
          },
        });
      }, 2000);
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to create your library. Please try again.";
      setError(errorMessage);
      showToast(errorMessage, "destructive", "Error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!success) return;

    const channel = new BroadcastChannel("email-verification");
    const handleVerificationSuccess = () => {
      navigate("/login", { replace: true });
    };

    channel.addEventListener("message", (event) => {
      if (event.data.type === "email-verified") {
        handleVerificationSuccess();
      }
    });

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "email_verified" && e.newValue === "true") {
        handleVerificationSuccess();
      }
    };
    window.addEventListener("storage", handleStorageChange);

    return () => {
      channel.close();
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [success, navigate]);

  if (success) {
    return (
      <AuthLayout
        headline="You're almost there."
        subtext="Just one more step to activate your library and start managing your collections."
      >
        <div className="text-center space-y-6 animate-fade-in-up">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-50">
            <CheckCircle2 className="h-8 w-8 text-green-600" />
          </div>

          <div>
            <h2 className="font-heading text-2xl font-bold text-slate-900">
              Library created successfully
            </h2>
            <p className="mt-2 text-sm text-slate-600">We've sent a verification email to:</p>
            <p className="mt-1 text-sm font-medium text-slate-900">{formData.adminEmail}</p>
          </div>

          <div className="rounded-lg border border-primary/20 bg-primary/5 p-4">
            <div className="flex items-start gap-3">
              <Mail className="h-5 w-5 text-primary mt-0.5 shrink-0" />
              <p className="text-sm text-primary text-left">
                Click the verification link in the email to activate your account. If you've already
                verified in another tab, this page will redirect automatically.
              </p>
            </div>
          </div>

          <Button variant="outline" onClick={() => navigate("/login")} className="w-full">
            Go to sign in
          </Button>
        </div>
      </AuthLayout>
    );
  }

  const { title, description } = stepMeta[currentStep - 1];

  return (
    <AuthLayout
      headline="Build your digital library."
      subtext="Set up your library management system in minutes. No technical knowledge required."
      maxWidth="xl"
      brandPanelWidth="default"
      verticalAlign="top"
    >
      <ToastContainer toasts={toasts} onClose={removeToast} />

      {/* Page header */}
      <div className="mb-8">
        <h2 className="font-heading text-2xl font-bold text-slate-900">Create your library</h2>
        <p className="mt-1 text-sm text-slate-500">Step {currentStep} of 4</p>
      </div>

      {/* Step indicator */}
      <StepIndicator currentStep={currentStep} onStepClick={goToStep} />

      {/* Step content card */}
      <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
        {/* Step header */}
        <div className="px-6 py-5 border-b border-slate-100">
          <h3 className="font-heading text-lg font-semibold text-slate-900">{title}</h3>
          <p className="mt-1 text-sm text-slate-500">{description}</p>
        </div>

        {/* Step body */}
        <div className="p-6">
          {currentStep === 1 && (
            <LibraryInfoStep data={formData} onChange={updateFormData} onNext={handleNext} />
          )}
          {currentStep === 2 && (
            <AdminInfoStep
              data={formData}
              onChange={updateFormData}
              onNext={handleNext}
              onBack={handleBack}
            />
          )}
          {currentStep === 3 && (
            <ContactInfoStep
              data={formData}
              onChange={updateFormData}
              onNext={handleNext}
              onBack={handleBack}
            />
          )}
          {currentStep === 4 && (
            <PreviewStep
              data={formData}
              onChange={updateFormData}
              onBack={handleBack}
              onSubmit={handleSubmit}
              onGoToStep={goToStep}
              isLoading={isLoading}
              error={error}
            />
          )}
        </div>
      </div>

      {/* Footer */}
      <p className="mt-6 text-center text-sm text-slate-500">
        Already have a library?{" "}
        <Link
          to="/login"
          className="text-primary font-medium hover:text-primary-600 transition-colors"
        >
          Sign in here
        </Link>
      </p>
    </AuthLayout>
  );
}
